import {createTypesenseClient} from "@buildwithflux/search";
import {useEffect, useState} from "react";
import type {Client} from "typesense";

import {
    resolveTypesenseSearchCredentials,
    type TypesenseAuthResponse,
    type TypesenseSearchCredentials,
} from "../typesenseCredentials";
import {getAccountsApiBase} from "../utils/apiBase";
import {fetchWithRetry} from "../utils/fetchWithRetry";

import type {ClientInitialization} from "./clientInitialization";

async function fetchScopedKeys(): Promise<TypesenseSearchCredentials> {
    const res = await fetchWithRetry(() =>
        fetch(`${getAccountsApiBase()}/accounts/auth/typesense`, {
            method: "POST",
            headers: {"Content-Type": "application/json"},
            body: JSON.stringify({}),
        }),
    );
    if (!res.ok) {
        throw new Error(`Typesense auth failed: ${res.status}`);
    }
    const data = (await res.json()) as TypesenseAuthResponse;
    return resolveTypesenseSearchCredentials(data);
}

/** Pass `enabled: false` on surfaces that never search these collections. */
export function useTypesenseClientInitialization(
    enabled = true,
): ClientInitialization<{readonly projects: Client; readonly parts: Client}> {
    const [result, setResult] = useState<ClientInitialization<{readonly projects: Client; readonly parts: Client}>>({
        client: null,
        status: enabled ? "loading" : "disabled",
    });

    useEffect(() => {
        if (!enabled) {
            setResult({client: null, status: "disabled"});
            return;
        }
        let cancelled = false;
        setResult({client: null, status: "loading"});

        fetchScopedKeys()
            .then((credentials) => {
                if (cancelled) return;
                setResult({
                    client: {
                        projects: createTypesenseClient({
                            host: credentials.projects.host,
                            port: 443,
                            protocol: "https",
                            apiKey: credentials.projects.apiKey,
                            cacheSearchResultsForSeconds: 10,
                        }),
                        parts: createTypesenseClient({
                            host: credentials.parts.host,
                            port: 443,
                            protocol: "https",
                            apiKey: credentials.parts.apiKey,
                            cacheSearchResultsForSeconds: 10,
                        }),
                    },
                    status: "ready",
                });
            })
            .catch((err) => {
                if (cancelled) return;
                // eslint-disable-next-line no-console
                console.error("Failed to initialize Typesense client:", err);
                setResult({client: null, status: "error"});
            });

        return () => {
            cancelled = true;
        };
    }, [enabled]);

    return result;
}
